import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

interface TourStep {
  title: string;
  body: string;
  cta?: { label: string; href: string };
}

const STEPS: TourStep[] = [
  {
    title: 'ברוך הבא ל-BookWise',
    body: 'מערכת אנליטיקה לראיית חשבון. בכמה דקות נראה לך את החלקים החשובים — תוכל לדלג בכל רגע.',
  },
  {
    title: '⌘K — מפתח לעיון מהיר',
    body: 'הקש ⌘K (או Ctrl+K) בכל מקום במערכת. תופיע חלונית חיפוש מהירה — קפיצה לכל דף, לכל לקוח, ולפעולות מהירות.',
  },
  {
    title: 'מצב לקוח',
    body: 'הסיידבר העליון מציג את הלקוח הפעיל, סטטוס הסקירה, וציון הסיכון. ניתן לעבור בין לקוחות בכל רגע.',
  },
  {
    title: 'ביקורת חוצה',
    body: 'דף /cross-audits סורק את כל הלקוחות שלך לאיתור הוצאות חוזרות שחסר בהן חודש. ניתן לסמן ממצאים, לפתוח משימה במשרד, או לבקש הסבר AI.',
    cta: { label: 'פתח ביקורת חוצה', href: '/cross-audits' },
  },
  {
    title: 'מאזן בוחן + ניירות עבודה',
    body: 'בדף /tb-audit תקבל מאזן בוחן מלא עם טור הערות לכל חשבון. הניירות נשמרים ל-(חברה, שנה, חשבון) ועוברים אוטומטית משנה לשנה.',
    cta: { label: 'פתח מאזן בוחן', href: '/tb-audit' },
  },
  {
    title: 'תזרים + רישומים מתואמים',
    body: 'דוח תזרים אוטומטי ב-/cash-flow-audit. רישומי AJE מתואמים ב-/aje עם בדיקת איזון אוטומטית.',
  },
  {
    title: 'AI לעזרה',
    body: 'לחיצה ימנית על שורה בביקורת חוצה → "הסבר עם AI". העלאת חשבונית ב-/doc-to-je תחזיר רישום יומן מאוזן מוצע.',
  },
];

const TOUR_KEY = 'maven_tour_seen_v1';

/** First-visit product tour. Shows on first page-load per device,
 * stores a flag in localStorage so it doesn't re-open. The user can
 * skip at any step; an "X" closes immediately. */
export default function ProductTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(TOUR_KEY);
      if (!seen) setOpen(true);
    } catch { /* ignore */ }
  }, []);

  const dismiss = (skipped = false) => {
    setOpen(false);
    try {
      localStorage.setItem(TOUR_KEY, skipped ? 'skipped' : 'completed');
    } catch { /* ignore */ }
  };

  if (!open) return null;
  const s = STEPS[step];

  return (
    <Dialog
      open={open}
      onClose={() => dismiss(true)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton size="small" onClick={() => dismiss(true)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <RocketLaunchIcon sx={{ color: 'primary.main', fontSize: 36 }} />
          <Box>
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
              צעד {step + 1} מתוך {STEPS.length}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
              {s.title}
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 15, lineHeight: 1.7 }}>
          {s.body}
        </Typography>
        {s.cta && (
          <Button
            variant="outlined"
            href={s.cta.href}
            onClick={() => dismiss(false)}
            sx={{ mt: 2 }}
          >
            {s.cta.label}
          </Button>
        )}
        <Box sx={{ display: 'flex', mt: 3, gap: 0.5 }}>
          {STEPS.map((_, i) => (
            <Box
              key={i}
              onClick={() => setStep(i)}
              sx={{
                cursor: 'pointer',
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: i <= step ? 'primary.main' : 'divider',
                transition: 'background-color .15s',
              }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => dismiss(true)} color="inherit">
          דלג
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          הקודם
        </Button>
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)}>
            הבא
          </Button>
        ) : (
          <Button variant="contained" onClick={() => dismiss(false)}>
            סיים
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
